import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import BlurReveal from '@ajat/design-system/BlurReveal';
import Button from '@ajat/design-system/Button';
import { ComponentPage, DemoCard, Demo } from '../shared';

export const meta = {
  component: BlurReveal,
  title: 'Blur Reveal',
  group: 'Foundations',
  importPath: '@ajat/design-system/BlurReveal',
  description:
    'Text entrance: each glyph rises out of a 2px blur with a small lift, staggered left to right; variant="block" lifts a whole element once after a delay, for sequencing eyebrow / copy / buttons around a headline. A CSS-only adaptation of the two 21st.dev blur-reveal components — the blur is ⅙ of the M2 effect blur (2px), lift, fade and de-blur on the UI ease-out (easing="spring" opts into the Gentle spring), duration 1.8 × the button time (720ms), lift small-s (glyphs and blocks alike); prefers-reduced-motion shows the text at once. Enter only (no exit). The full text stays readable for assistive tech; the glyphs are aria-hidden. Used for the OVER/LL hero headline (HeroBannerV3 revealTitle).',
  props: [
    ['children', 'node', '—', 'Text to reveal: a string, or elements (an accent span) wrapping strings — wrappers are kept'],
    ['as', 'elementType', "'p'", 'Rendered tag (h1, h2, p, …)'],
    ['className', 'string', "''", 'Additional CSS class — put the typo-* class here'],
    ['delay', 'number', '0', 'Wait before the first glyph, ms'],
    ['stagger', 'number', '16', 'Gap between consecutive glyphs, ms'],
    ['duration', 'number', '720', 'Per-unit duration, ms (1.8 × --transition-time-button)'],
    ['variant', "'glyphs' | 'block' | 'lines'", "'glyphs'", 'glyphs — split text, staggered units; block — children untouched, the whole element rises once after delay; lines — each top-level chunk rises out of a baseline mask (no fade/blur)'],
    ['unit', "'glyph' | 'word'", "'glyph'", 'glyphs variant: a character or a whole word per stagger step'],
    ['easing', "'ui' | 'spring'", "'ui'", 'UI ease-out (--transition-function-ui) or the DS Gentle spring (--transition-function-drawer)'],
    ['distance', 'string', 'small-s', 'Movement distance override (CSS length)'],
    ['blurRadius', 'string', '2px', 'Enter blur override (CSS length)'],
    ['lift', 'bool', 'true', 'false — fade out of the blur in place, no movement (hero image)'],
    ['blur', 'bool', 'true', 'false — no blur filter, fade (+ lift) only; for video'],
    ['style', 'object', '—', 'Inline styles merged with the timing variables'],
  ],
  usage: [
    {
      title: 'A headline with an accent',
      code: `import BlurReveal from '@ajat/design-system/BlurReveal';

<BlurReveal as="h1" className="typo-h1-editorial">
  Official uniform of <span className="accent">unofficial behavior</span>
</BlurReveal>`,
    },
    {
      title: 'Slower, after a beat',
      code: `<BlurReveal as="p" className="typo-body-lg" delay={300} stagger={35}>
  Designa din studentoverall från grunden.
</BlurReveal>`,
    },
    {
      title: 'Sequencing a section (block variant)',
      code: `<BlurReveal variant="block" as="p" className="typo-body" delay={0}>Designed in Stockholm</BlurReveal>
<BlurReveal as="h1" className="typo-h1-editorial" delay={80}>Official uniform of</BlurReveal>
<BlurReveal variant="block" as="p" className="typo-body-lg" delay={400}>Designa din overall…</BlurReveal>
<BlurReveal variant="block" delay={520}>
  <Button size="secondary" variant="main">Designa editions</Button>
</BlurReveal>`,
    },
  ],
};

export default function BlurRevealPage() {
  const [run, setRun] = useState(0);
  const replay = (
    <Button size="tertiary" variant="outline" iconLeft={<RotateCcw />} onClick={() => setRun((n) => n + 1)}>
      Replay
    </Button>
  );

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Editorial headline" hint="Keyframes restart on mount — Replay remounts the text">
        {replay}
        <Demo caption="h1 · typo-h1-editorial · accent span kept">
          <BlurReveal key={`a${run}`} as="h1" className="typo-h1-editorial" style={{ margin: 0, textTransform: 'uppercase' }}>
            Official uniform of{' '}
            <span style={{ color: 'var(--color-brand-primary-alternative)' }}>unofficial behavior</span>
          </BlurReveal>
        </Demo>
        <Demo caption="block variant · eyebrow 0ms → copy 400ms → button 520ms">
          <div key={`c${run}`} className="flex flex-col gap-medium-s">
            <BlurReveal variant="block" as="p" className="typo-body" style={{ margin: 0, color: 'var(--color-gray-6)' }}>
              DESIGNED IN STOCKHOLM
            </BlurReveal>
            <BlurReveal variant="block" as="p" className="typo-body-lg" delay={400} style={{ margin: 0 }}>
              Designa din studentoverall från grunden eller börja med en edition.
            </BlurReveal>
            <BlurReveal variant="block" delay={520}>
              <Button size="secondary" variant="main">Designa editions</Button>
            </BlurReveal>
          </div>
        </Demo>
        <Demo caption="p · typo-body-lg · delay 300ms, stagger 35ms">
          <BlurReveal key={`b${run}`} as="p" className="typo-body-lg" delay={300} stagger={35} style={{ margin: 0 }}>
            Designa din studentoverall från grunden eller börja med en edition.
          </BlurReveal>
        </Demo>
      </DemoCard>
    </ComponentPage>
  );
}
